import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, EMPTY, filter, map, Observable, retry, Subject, switchMap, take, takeUntil, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ReportCategory } from '../../insights/administration/reports/reports.component';
import { AuditService } from './audit.service';
import { EmbedconfigService, IEmbedConfig } from './embedconfig.service';
import { SessionStorageService } from './storage.service';
import { UserService } from './user.service';

export interface IModelMetaData {
	createdAt?: number;
	createdBy?: string;
	updatedAt?: number;
	updatedBy?: string;
	version?: number;
}

export interface IModelBase extends IModelMetaData {
	id?: string;
}

export interface IExternalUrl extends IModelBase {
	name: string;
	url?: string;
	isDeleted?: boolean;
	categoryId?: string | null;
	categoryName?: string;
	createdDate?: Date;
	updatedDate?: Date;
	categorySortOrder?: number;
}

export type IExternalUrlList = IExternalUrl[];

export interface IExternalUrlsResponse extends IModelBase {
	result: IExternalUrlList;
}

export interface IReport extends IModelBase {
	name: string;
	reportId?: string;
	url?: string;
	groupId?: string;
	externalId?: string;
	externalUrlId?: string;
	isDeleted?: boolean;
	categoryId?: string | null;
	categoryName?: string;
	createdDate?: Date;
	updatedDate?: Date;
	categorySortOrder?: number;
}

export type IReportList = IReport[];

interface IReportsResponse extends IModelBase {
	result: IReportList;
}

export interface IDeleteReport extends IModelBase {
	isDeleted?: boolean;
	name?: string;
}

export interface IUserReportRelation {
	userId: string;
	reportId: string;
}

export interface IUserReportRelations {
	userId: string;
	userReports: IUserReportRelation[];
}

export interface S3SignedUrl {
	expires: number;
	key: string;
	method: 'PUT' | 'PATCH' | 'POST';
	requiredHeaders: HttpHeaders;
	url: string;
}

export interface S3SignedUrlResultSet {
	result: S3SignedUrl;
}

export interface InsightsAccount {
	id: string;
	accountId: string;
	groupIds: string[];
	ipWhiteList: string[] | null;
	domainWhiteList: string[] | null;
	createdAt: number;
	updatedAt: number;
}

@Injectable({
	providedIn: 'root',
})
export class ReportService implements OnDestroy, OnInit {
	private reportsSubject$: BehaviorSubject<IReport[]>;
	public reports$: Observable<IReport[]>;
	private reportSubject$: BehaviorSubject<IReport | null>;
	public report$: Observable<IReport | null>;
	private reportCategoriesSubject$: BehaviorSubject<ReportCategory[]>;
	public reportCategories$: Observable<ReportCategory[]>;
	unsubscribe: Subject<void> = new Subject<void>();
	reportCategories: ReportCategory[] = [];
	private userReportsSubject$: BehaviorSubject<IReport[]>;
	public userReports$: Observable<IReport[]>;

	private requestQueue: Subject<Observable<IEmbedConfig>> = new Subject(); // cjs
	private requestQueue$: Observable<Observable<IEmbedConfig>> = this.requestQueue.asObservable();

	private _accountCancel$ = new Subject<void>();

	constructor(
		private http: HttpClient,
		private embedService: EmbedconfigService,
		private userService: UserService,
		private auditService: AuditService,
		private sessionStorageService: SessionStorageService,
	) {
		this.reportsSubject$ = new BehaviorSubject<IReport[]>([]);
		this.reports$ = this.reportsSubject$.asObservable();

		this.userReportsSubject$ = new BehaviorSubject<IReport[]>([]);
		this.userReports$ = this.userReportsSubject$.asObservable();

		this.reportSubject$ = new BehaviorSubject<IReport | null>(null);
		this.report$ = this.reportSubject$.asObservable();

		this.reportCategoriesSubject$ = new BehaviorSubject<ReportCategory[]>([]);
		this.reportCategories$ = this.reportCategoriesSubject$.asObservable();

		// If only one report, use that report
		this.reports$
			.pipe(
				takeUntil(this.unsubscribe),
				filter((v) => v.length === 1),
			)
			.subscribe((v) => {
				this.report = v[0];
			});

		// Set new active embedconfig when new report has been selected
		this.report$.pipe(takeUntil(this.unsubscribe)).subscribe((v) => {
			if (v) {
				console.log(`setting embedconfig for report (${v.externalId})`);
				this.embedService.setLocalEmbedConfig('report', v.externalId, v.groupId);
			}
		});

		// Silently create embedconfigs for all new reports
		this.reportsSubject$.pipe(takeUntil(this.unsubscribe)).subscribe((v) => {
			console.log(`reports array changed, setting new embedconfig for reports`);

			const isAuth = this.sessionStorageService.getItem('User.Email') !== null && this.sessionStorageService.getItem('User.Email').length > 0;

			const biReports = v.filter((r) => r.groupId !== 'ext-url');

			if (biReports.length > 0 && isAuth) {
				const embedConfigBatchObs = this.embedService.getEmbedConfigBatch('report', biReports);
				embedConfigBatchObs.subscribe((embedConfigs) => {
					if (embedConfigs !== undefined) {
						embedConfigs.forEach((embedConfig) => {
							this.embedService.addEmbedConfigToArray(embedConfig);
						});
					}
				});
			}
		});

		this.report$.pipe(takeUntil(this.unsubscribe)).subscribe((v) => {
			if (v && v.groupId !== 'ext-url') {
				const reportId = v.reportId ? v.reportId : v.id;
				console.log(`Report ${reportId} has been selected`);
				this.auditService
					.LogEvent({
						service: 'insights',
						resource: 'Report',
						event: 'Read',
						data: {
							id: reportId,
							version: undefined,
						},
					})
					.subscribe((c) => console.log(c));
			}
		});

		this.userService.accountChanged$.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this._accountCancel$.next();
			this.reportCategories = [];
			this.reportsSubject$.next([]);
			this.userReportsSubject$.next([]);
			this.reportSubject$.next(null);
			this.reportCategoriesSubject$.next([]);
		});
	}

	ngOnInit() {}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	public get report(): IReport | null {
		return this.reportSubject$.value || null;
	}

	public set report(v: IReport | null) {
		this.reportSubject$.next(v);

		if (v) {
			this.sessionStorageService.set('User.Report', JSON.stringify(v));
		} else {
			this.sessionStorageService.remove('User.Report');
		}
	}

	public set userReports(v: IReport[] | null) {
		this.userReportsSubject$.next(v);
	}

	public get userReports() {
		return this.userReportsSubject$.value;
	}

	public synchronizeWithPowerBI(type: string, isAdmin: boolean = false, reload: boolean = true): void {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		const body = {
			type,
			account: account.id,
		};
		this.http
			.post<void>(`${environment.awsEnvUri}/insights/powerbi`, body, {
				headers,
			})
			.pipe(retry({ count: 5, delay: 1000 }), catchError(this.handleError))
			.subscribe(() => {
				console.log('synchronize called...');
				if (reload) this.fetchUserReports(null, isAdmin);
			});
	}

	public fetchUserReports(id?: string, isAdmin: boolean = false): void {
		if (id === undefined) {
			id = this.userService.impersonateUser?.id;
		}
		if (!isAdmin) {
			isAdmin = this.userService.impersonateUser ? false : JSON.parse(this.sessionStorageService.get('User.Permissions')).findIndex((x) => x === 'insights:ReportCreate') > 0;
		}

		let reports: IReport[] = [];
		const account = this.userService.account;
		if (account) {
			console.log(`fetching reports for account...${account.id}${account.name}`);
			const headers = {
				Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
				'x-account-id': account.id,
			};
			let userId = this.sessionStorageService.get('User.IAM.Id');
			if (id) {
				userId = id;
			}

			let reportsEndpoint = `${environment.awsEnvUri}/insights/users/${userId}/reports?activeOnly=true`;
			if (isAdmin) {
				reportsEndpoint = `${environment.awsEnvUri}/insights/reports?activeOnly=true`;
			}

			let extUrlsEndpoint = `${environment.awsEnvUri}/insights/users/${userId}/externalurls?activeOnly=true`;
			if (isAdmin) {
				extUrlsEndpoint = `${environment.awsEnvUri}/insights/externalurls?activeOnly=true`;
			}

			combineLatest([
				this.http.get<IReportsResponse>(reportsEndpoint, { headers }).pipe(retry(1), catchError(this.handleError)),
				this.http
					.get<IExternalUrlsResponse>(extUrlsEndpoint, {
						headers,
					})
					.pipe(
						retry(1),
						catchError(this.handleError),
						map((val: IExternalUrlsResponse) => {
							const mapped: IExternalUrlsResponse = {
								result: val.result,
							};

							mapped.result.forEach((x) => (x.url = decodeURIComponent(x.url)));
							return mapped;
						}),
					),
			])
				.pipe(takeUntil(this._accountCancel$))
				.subscribe((data) => {
					if (data[0] && data[1]) {
						const extUrlsReports: IReport[] = data[1].result;
						extUrlsReports.forEach((r) => {
							r.externalId = r.url;
						});
						reports = [...data[0].result, ...extUrlsReports];
						this.reportsSubject$.next(reports);
						this.embedService.updateLocalEmbedConfig(reports);
					}
				});
		}
	}

	public fetchUserReportsById(id: string): Observable<IReportsResponse> {
		console.log('fetching reports...');
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http.get<IReportsResponse>(`${environment.awsEnvUri}/insights/users/${id}/reports?activeOnly=true`, { headers }).pipe(retry(1), catchError(this.handleError));
	}

	public fetchReports(activeOnly: boolean = false): Observable<IReportsResponse> {
		const account = this.userService.account;
		if (!account) {
			return EMPTY;
		}
		let activeReportOnly: string = '';
		if (activeOnly) {
			activeReportOnly = '?activeOnly=true';
		}
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http
			.get<IReportsResponse>(`${environment.awsEnvUri}/insights/reports${activeReportOnly}`, {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public addUserReports(userReportRelations: IUserReportRelations): Observable<IReportsResponse> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		const userId = this.sessionStorageService.get('User.IAM.Id');
		return this.http.post<IReportsResponse>(`${environment.awsEnvUri}/insights/users/${userId}/reports`, userReportRelations, { headers }).pipe(retry(1), catchError(this.handleError));
	}

	public deleteReports(deleteReports: IDeleteReport[]): Observable<IReportsResponse> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http
			.patch<IReportsResponse>(`${environment.awsEnvUri}/insights/reports`, deleteReports, {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public permanentlyDeleteReport(report: IReport): Observable<string> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http
			.delete<string>(`${environment.awsEnvUri}/insights/reports/${report.id}`, {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public getPowerBiMetaData(reportName: string) {
		return this.http
			.get<{ result: InsightsAccount }>(`${environment.awsEnvUri}/insights/accounts`, {
				headers: this.getHeaders(),
			})
			.pipe(
				map((d) => d.result),
				switchMap((account) => {
					const formData = new FormData();
					formData.append('ReportName', encodeURIComponent(reportName));
					formData.append('GroupId', account.groupIds.shift());
					return this.http
						.post<S3SignedUrlResultSet>(`${environment.awsEnvUri}/insights/PowerBIFile`, formData, {
							headers: this.getHeaders(),
						})
						.pipe(map((d) => d.result));
				}),
			);
	}

	public loadReportCategories(clearState = false): Observable<ReportCategory[]> {
		if (clearState) this.reportCategories = [];

		// Removing this, as caching of categories will not work across accounts
		// if (this.reportCategories.length) {
		//   return of(this.reportCategories);
		// }

		return this.userService.account$.pipe(
			take(1),
			switchMap(() =>
				this.http
					.get<ReportCategory[]>(`${environment.awsEnvUri}/insights/categories`, {
						headers: this.getHeaders(),
					})
					.pipe(
						map((res) => res['result']),
						tap((c) => {
							this.reportCategories = c; // Set simpel state i en tap "side effect" for at undgå at relaode listen
						}),
						catchError(this.handleError),
					),
			),
			// shareReplay()
		);
	}

	public addReportCategory(name: string, id: string, sortOrder: number): Observable<any> {
		const body = { name, sortOrder };
		return this.http
			.post(`${environment.awsEnvUri}/insights/categories`, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public getReportCategory(categoryId: string): ReportCategory {
		return this.reportCategories.filter((category) => category.id === categoryId)[0];
	}

	public deleteReportCategory(categoryId: string): Observable<any> {
		return this.http
			.delete(`${environment.awsEnvUri}/insights/categories/${categoryId}`, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	// Used when saving sort order
	public saveCategories(categories: ReportCategory[]) {
		const output = categories
			.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : a.sortOrder === b.sortOrder ? 0 : -1))
			.map((category, index) => ({
				id: category.id,
				sortOrder: index + 1,
				name: category.name,
			})); // Normaliser rækkefølgen
		return this.http
			.put(`${environment.awsEnvUri}/insights/categories/`, output, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public assignCategoryToSelectedReports(category: ReportCategory, selectedReports: IReport[], selectedExtUrls: IReport[]) {
		const payloadReports = selectedReports.map((report) => ({
			id: report.id,
			categoryId: category.id,
		}));
		const payloadExtUrls = selectedExtUrls.map((extUrl) => ({
			id: extUrl.id,
			categoryId: category.id,
		}));

		combineLatest([
			this.http
				.patch(`${environment.awsEnvUri}/insights/reports/`, payloadReports, {
					headers: this.getHeaders(),
				})
				.pipe(retry(1), catchError(this.handleError)),
			this.http
				.patch(`${environment.awsEnvUri}/insights/externalurls/`, payloadExtUrls, {
					headers: this.getHeaders(),
				})
				.pipe(retry(1), catchError(this.handleError)),
		]).subscribe(() => {
			this.fetchUserReports();
		});
	}

	public updateCategory(categoryId: string, newName: string) {
		const body = {
			id: categoryId,
			name: newName,
		};
		return this.http
			.patch(`${environment.awsEnvUri}/insights/categories/`, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	private handleError(error: { error: { message: string }; status: any; message: any }) {
		console.log('CJS http error', error);
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		if (error.status !== 403) {
			console.log(errorMessage);
			return throwError(() => new Error(errorMessage));
		}
		return [];
	}

	private getHeaders() {
		const account = this.userService.account;
		return {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
	}
}
